import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import type { ScrollViewInstance } from "react-native";
import {
  Linking,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  ScrollView,
  Text,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { PublicShowSkeleton } from "@/components/shows/PublicShowSkeleton";
import { ShowListingBody } from "@/components/shows/ShowListingBody";
import { Button } from "@/components/ui/Button";
import { Screen } from "@/components/ui/Screen";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { usePublicShow } from "@/hooks/usePublicShow";
import { publicShowVendorStorefrontUrl } from "@/hooks/usePublicShowInventory";
import { useLocalShowPullSheets } from "@/hooks/usePullSheet";
import { useShowHuntList } from "@/hooks/useShowHuntList";
import { loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";

const scrollOffsets = new Map<string, number>();

function one(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function showReferrerClass(): string {
  if (Platform.OS !== "web") return "native";
  const source = new URLSearchParams(window.location.search).get("source");
  if (source === "poster_qr") return "poster_qr";
  if (!document.referrer) return "direct";
  try {
    return new URL(document.referrer).origin === window.location.origin
      ? "internal"
      : "shared_link";
  } catch {
    return "direct";
  }
}

export default function PublicShowScreen() {
  const { q: rawQuery, showId: rawShowId } = useLocalSearchParams<
    { q?: string; showId: string; }
  >();
  const showId = one(rawShowId);
  const urlQuery = one(rawQuery) ?? "";
  const insets = useSafeAreaInsets();
  const { show, savedAt, isLoading, error, refetch } = usePublicShow(showId);
  // Often a cold launch from a show QR or shared link. An error also ends the skeleton, since retry
  // is then the action, and the inventory query fills in after the page is readable.
  useMarkInteractive(!isLoading);
  const localLists = useLocalShowPullSheets(show?.public_id ?? showId);
  const hunt = useShowHuntList(showId).hunt;
  const scrollRef = useRef<ScrollViewInstance | null>(null);
  const restoredKey = useRef("");
  const [analytics, setAnalytics] = useState<Awaited<ReturnType<typeof loadShowAnalytics>> | null>(
    null,
  );
  const openedShow = useRef("");
  const searchedKey = useRef("");

  useEffect(() => {
    if (!show || openedShow.current === show.public_id) return undefined;
    let active = true;
    const showEndsAt = (show.sessions ?? []).map((session) => session.ends_at).sort().at(-1)
      ?? null;
    void loadShowAnalytics({ showId: show.public_id, showEndsAt }).then((loadedAnalytics) => {
      if (!active) return;
      openedShow.current = show.public_id;
      setAnalytics(loadedAnalytics);
      loadedAnalytics.showOpened(show.public_id, showReferrerClass(), savedAt !== null);
    });
    return () => {
      active = false;
    };
  }, [savedAt, show]);

  const scrollKey = showId ?? "";
  useEffect(() => {
    if (!show || restoredKey.current === scrollKey) return undefined;
    restoredKey.current = scrollKey;
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: scrollOffsets.get(scrollKey) ?? 0, animated: false });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollKey, show]);
  const rememberScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffsets.set(scrollKey, event.nativeEvent.contentOffset.y);
  };

  if (isLoading) return <PublicShowSkeleton label="Loading Show…" testID="show-screen" />;

  if (error || !show) {
    return (
      <Screen
        testID="show-screen"
        className="events-page items-center justify-center px-6"
        role="main"
      >
        <Text className="text-center text-lg font-title text-foreground">Show unavailable</Text>
        <Text className="mt-2 text-center font-body text-muted-foreground">
          We couldn’t load the details right now. Check your connection and try again.
        </Text>
        <View className="mt-5">
          <Button title="Try again" variant="outline" onPress={() => void refetch()} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen testID="show-screen" className="events-page" role="main">
      {/* This screen sets no per-Show `<title>`; `shows/_layout.tsx` owns the shared head tags. */}
      {
        /* No chrome bar: the page's color comes from the Organizer's poster, and the only control
        visitors used, the theme switch, sits inside `ShowListingBody`'s action row. */
      }
      <ScrollView
        ref={scrollRef}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: insets.bottom + 24 }}
        onScroll={rememberScroll}
        scrollEventThrottle={16}
      >
        <View className="px-4" style={{ paddingTop: insets.top + 16 }}>
          {savedAt
            ? (
              <View
                testID="show-stale-notice"
                accessibilityLiveRegion="polite"
                className="mt-4 w-full self-center rounded-xl bg-warning-soft p-4"
                style={{ maxWidth: 1180 }}
              >
                <Text className="font-body text-foreground">
                  Saved at{" "}
                  {new Date(savedAt).toLocaleString()}. Table assignments may have changed since
                  this was saved.
                </Text>
              </View>
            )
            : null}
          <ShowListingBody
            show={show}
            initialQuery={urlQuery}
            onSearchResult={({ query, cards, zeroResultCause }) => {
              if (Platform.OS !== "web") router.setParams({ q: query || undefined });
              if (!analytics) return;
              // `ShowListingBody` reports every settle with no memory of which ones already got
              // here, so this key is what keeps a repeated settle for the same query (a background
              // refetch, e.g.) from reporting twice.
              const key = `${show.public_id}:${query}`;
              if (searchedKey.current === key) return;
              searchedKey.current = key;
              analytics.showSearched({
                status: "available",
                showId: show.public_id,
                resultCardCount: cards.length,
                ...(cards.length === 1
                  ? { catalogCardId: cards[0].card_id }
                  : { unresolvedQueryLength: query.length }),
                ...(zeroResultCause ? { zeroResultCause } : {}),
              });
            }}
            hunt={{
              count: hunt?.cards.length ?? 0,
              onOpen: () => router.push(`/shows/${encodeURIComponent(show.public_id)}/hunt`),
            }}
            pullSheets={localLists.data ?? []}
            onOpenPullSheet={(participationId) =>
              // The storefront holding this list is on another origin, so the server resolves the
              // URL; a client push would land on the events host and start a second list for the
              // same table (ADR-0092).
              void Linking.openURL(
                publicShowVendorStorefrontUrl(show.public_id, participationId, ""),
              )}
          />
        </View>
      </ScrollView>
    </Screen>
  );
}
